import { useTranslation } from "react-i18next";

import { FeedAttachmentRow } from "./FeedAttachmentRow";
import { useFeedCompactLayout } from "./FeedCompactLayoutContext";
import { PunttAvatar } from "./PunttAvatar";

import {
  AvatarWithInitials,
  AVATAR_SIZE_BIG,
  AVATAR_SIZE_NORMAL,
} from "../../../components/AvatarWithInitials";
import {
  type ProjectFeedAttachment,
  type ProjectFeedEvent,
} from "../../../services/askPunttProjectFeed.types";
import { toTitleCase } from "../../../utils/titleCase";

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function actorDisplayName(
  event: ProjectFeedEvent,
  memberFallback: string,
): string {
  const actor = event.actor;
  if (actor?.name) return actor.name;
  if (actor?.kind === "puntt") return "Puntt";
  if (actor?.kind === "system") return "Puntt";
  return memberFallback;
}

/**
 * Generic feed post: author row (avatar, name, role subtitle, timestamp),
 * the post body, and any attachment rows. Specialized event types (imports,
 * memory, suggestions, coaching, version diffs) render their own cards in
 * renderProjectFeedCard; FeedPost is the fallback for human + Puntt narrative
 * posts (project_created, owner_kickoff, version_added, review_started,
 * user_post, …).
 *
 * When the chat column is narrow, AskPunttChat provides compact layout via
 * FeedCompactLayoutContext so avatars match ChatMessage (size 6, inline header).
 */
export function FeedPost({
  event,
  onOpenAttachment,
}: {
  event: ProjectFeedEvent;
  onOpenAttachment?: (attachment: ProjectFeedAttachment) => void;
}) {
  const { t } = useTranslation("ui");
  const feedCompactLayout = useFeedCompactLayout();
  const actor = event.actor;
  const isPuntt = actor?.kind === "puntt" || actor?.kind === "system";
  const name = actorDisplayName(event, t("feedPost.memberFallbackName"));
  const role = actor?.role
    ? toTitleCase(actor.role)
    : isPuntt
      ? t("feedPost.aiReviewerRole")
      : undefined;
  const time = formatTime(event.createdAt);
  const avatarSize = feedCompactLayout ? AVATAR_SIZE_NORMAL : AVATAR_SIZE_BIG;
  const avatar = isPuntt ? (
    <PunttAvatar size={avatarSize} />
  ) : (
    <AvatarWithInitials
      size={avatarSize}
      name={name}
      avatar={actor?.avatar ?? undefined}
      colorKey={actor?.id ?? undefined}
      backgroundColor={actor?.avatarBackgroundColor ?? undefined}
      textColor={actor?.avatarTextColor ?? undefined}
    />
  );

  const body = (
    <>
      {event.body && (
        <p className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-puntt-neutral-gray-12">
          {event.body}
        </p>
      )}
      {event.attachments && event.attachments.length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {event.attachments.map((a) => (
            <FeedAttachmentRow
              key={`${a.fileId}-${a.versionNumber ?? "x"}`}
              attachment={a}
              onOpen={onOpenAttachment}
            />
          ))}
        </div>
      )}
    </>
  );

  if (feedCompactLayout) {
    return (
      <article
        className="mb-4"
        data-testid={`feed-post-${event._id}`}
        data-event-type={event.type}
        data-feed-compact="true"
      >
        <div
          className="mb-1 flex min-w-0 items-center gap-2"
          data-testid={`feed-post-author-header-${event._id}`}
        >
          <div className="shrink-0">{avatar}</div>
          <span className="truncate text-sm font-semibold text-puntt-neutral-gray-12">
            {name}
          </span>
          {role && (
            <span
              className="shrink-0 text-xs text-puntt-neutral-gray-10"
              data-testid={`feed-post-role-${event._id}`}
            >
              {role}
            </span>
          )}
          {time && (
            <span
              className="shrink-0 text-xs text-puntt-neutral-gray-9"
              data-testid={`feed-post-timestamp-${event._id}`}
            >
              {time}
            </span>
          )}
        </div>
        <div className="min-w-0">{body}</div>
      </article>
    );
  }

  return (
    <article
      className="flex gap-3"
      data-testid={`feed-post-${event._id}`}
      data-event-type={event.type}
    >
      <div
        className="shrink-0 pt-0.5"
        data-testid={`feed-post-avatar-column-${event._id}`}
      >
        {avatar}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-puntt-neutral-gray-12">
            {name}
          </span>
          {role && (
            <span
              className="text-xs text-puntt-neutral-gray-10"
              data-testid={`feed-post-role-${event._id}`}
            >
              {role}
            </span>
          )}
          {time && (
            <span
              className="shrink-0 text-xs text-puntt-neutral-gray-9"
              data-testid={`feed-post-timestamp-${event._id}`}
            >
              {time}
            </span>
          )}
        </div>
        {body}
      </div>
    </article>
  );
}
